/**
 * Menu / kitchen ordering windows derived from business operating_hours.
 * Mirrors backend/lib/menuAvailability.js for storefront UX.
 */
import { WEEKDAYS, normalizeOperatingHours } from "@/lib/operatingHours";

export const KITCHEN_CLOSE_MINUTES = 10;

const LUNCH_NAME_RE = /^(lunch(\s+menu)?|mittagskarte|mittagsmen[uü]|mittags\s*karte)$/i;

export function parseHmToMinutes(hm) {
  const match = String(hm || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function formatMinutesToHm(totalMinutes) {
  const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function weekdayKeyFromDate(date = new Date()) {
  return WEEKDAYS[(date.getDay() + 6) % 7];
}

export function isWeekday(date = new Date()) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

export function isLunchMenuCategory(category) {
  const name = String(category?.name || category || "").trim();
  return LUNCH_NAME_RE.test(name);
}

export function getTodayHoursRow(operatingHours, date = new Date()) {
  const normalized = normalizeOperatingHours(operatingHours || []);
  const key = weekdayKeyFromDate(date);
  return normalized.find((row) => row.day === key) || { day: key, open: false, periods: [] };
}

export function getOpenPeriodsForDay(dayRow) {
  if (!dayRow?.open) return [];
  return (dayRow.periods || [])
    .map((period) => ({
      from: String(period?.from || "").trim(),
      to: String(period?.to || "").trim(),
      fromMinutes: parseHmToMinutes(period?.from),
      toMinutes: parseHmToMinutes(period?.to),
    }))
    .filter((period) => period.fromMinutes != null && period.toMinutes != null);
}

export function kitchenCutoffMinutes(period, bufferMinutes = KITCHEN_CLOSE_MINUTES) {
  if (period?.toMinutes == null) return null;
  return period.toMinutes - Math.max(0, Number(bufferMinutes) || 0);
}

export function getActivePeriod(periods, date = new Date()) {
  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  return (periods || []).find((period) => {
    const from = period.fromMinutes;
    const to = period.toMinutes;
    if (from == null || to == null) return false;
    if (from <= to) return nowMinutes >= from && nowMinutes < to;
    return nowMinutes >= from || nowMinutes < to;
  }) || null;
}

export function getOrderingWindow(business, date = new Date(), bufferMinutes = KITCHEN_CLOSE_MINUTES) {
  const dayRow = getTodayHoursRow(business?.operating_hours, date);
  if (!dayRow.open) {
    return { allowed: false, reason: "closed_day", activePeriod: null, kitchenCutoffHm: null, dayRow, periods: [] };
  }
  const periods = getOpenPeriodsForDay(dayRow);
  if (!periods.length) {
    return { allowed: false, reason: "closed_day", activePeriod: null, kitchenCutoffHm: null, dayRow, periods };
  }
  const activePeriod = getActivePeriod(periods, date);
  if (!activePeriod) {
    return { allowed: false, reason: "between_periods", activePeriod: null, kitchenCutoffHm: null, dayRow, periods };
  }
  const cutoff = kitchenCutoffMinutes(activePeriod, bufferMinutes);
  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  if (cutoff == null || nowMinutes >= cutoff) {
    return {
      allowed: false,
      reason: "kitchen_closed",
      activePeriod,
      kitchenCutoffHm: cutoff == null ? null : formatMinutesToHm(cutoff),
      dayRow,
      periods,
    };
  }
  return {
    allowed: true,
    reason: null,
    activePeriod,
    kitchenCutoffHm: formatMinutesToHm(cutoff),
    dayRow,
    periods,
  };
}

export function isProductOrderableNow(product, category, business, date = new Date(), bufferMinutes = KITCHEN_CLOSE_MINUTES) {
  const window = getOrderingWindow(business, date, bufferMinutes);
  if (!window.allowed) {
    return { orderable: false, reason: window.reason, window };
  }

  if (!isLunchMenuCategory(category)) {
    return { orderable: true, reason: null, window };
  }

  if (!isWeekday(date)) {
    return { orderable: false, reason: "lunch_weekend", window };
  }

  const firstPeriod = window.periods[0];
  const active = window.activePeriod;
  const isFirst = Boolean(
    firstPeriod
    && active
    && active.fromMinutes === firstPeriod.fromMinutes
    && active.toMinutes === firstPeriod.toMinutes
  );
  if (!isFirst) {
    return { orderable: false, reason: "lunch_evening", window };
  }

  return { orderable: true, reason: null, window };
}

export function kitchenCloseMinutesForBusiness(business) {
  const value = Number(business?.kitchen_close_minutes);
  if (Number.isFinite(value) && value >= 0) return value;
  return KITCHEN_CLOSE_MINUTES;
}
